/**
 * Formulário do anexo do catálogo (#520). O texto usa o mesmo editor do texto padronizado, e o
 * servidor sanitiza ao gravar com a mesma política — tabela e negrito passam.
 */
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import { EditorDeTextoRico } from '../../../components/forms';
import {
  ROTULO_DO_DOCUMENTO,
  TIPOS_COM_ANEXO,
  type DocumentAttachment,
  type DocumentAttachmentPayload,
  type DocumentAttachmentType,
} from '../../../types/documentAttachment';

interface DocumentAttachmentFormValues {
  documentType: DocumentAttachmentType;
  title: string;
  normReference: string;
  numeral: string;
  displayOrder: string;
  isActive: 'true' | 'false';
  content: string;
}

/** Sem as marcações e com `&nbsp;` virando espaço, sobra texto? É a mesma conta do servidor. */
function temTextoVisivel(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
}

const validationSchema: yup.ObjectSchema<DocumentAttachmentFormValues> = yup.object({
  documentType: yup.mixed<DocumentAttachmentType>().oneOf([...TIPOS_COM_ANEXO]).required('Escolha o documento'),
  title: yup.string().trim().required('O título do anexo é obrigatório').max(255, 'Máximo de 255 caracteres'),
  normReference: yup.string().defined().max(100, 'Máximo de 100 caracteres'),
  numeral: yup.string().defined().max(20, 'Máximo de 20 caracteres'),
  displayOrder: yup
    .string()
    .required('Informe a ordem')
    .matches(/^\d+$/, 'Use um número inteiro entre 0 e 9999')
    .test('ordem-no-teto', 'Use um número inteiro entre 0 e 9999', (valor) => Number(valor) <= 9999),
  isActive: yup.mixed<'true' | 'false'>().oneOf(['true', 'false']).required(),
  content: yup
    .string()
    .required('O texto do anexo é obrigatório')
    .test('texto', 'O texto do anexo é obrigatório', temTextoVisivel),
});

function paraValores(anexo?: DocumentAttachment): DocumentAttachmentFormValues {
  return {
    documentType: anexo?.documentType ?? 'pcmso',
    title: anexo?.title ?? '',
    normReference: anexo?.normReference ?? '',
    numeral: anexo?.numeral ?? '',
    displayOrder: String(anexo?.displayOrder ?? 0),
    isActive: anexo?.isActive === false ? 'false' : 'true',
    content: anexo?.content ?? '',
  };
}

function paraPayload(valores: DocumentAttachmentFormValues): DocumentAttachmentPayload {
  return {
    documentType: valores.documentType,
    title: valores.title.trim(),
    normReference: valores.normReference.trim() === '' ? null : valores.normReference.trim(),
    numeral: valores.numeral.trim() === '' ? null : valores.numeral.trim(),
    displayOrder: Number(valores.displayOrder),
    isActive: valores.isActive === 'true',
    content: valores.content,
  };
}

interface DocumentAttachmentFormProps {
  anexo?: DocumentAttachment;
  onSubmit: (data: DocumentAttachmentPayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Recusa do servidor — título repetido no mesmo documento. Fica junto do botão de salvar. */
  erro?: string | null;
}

export const DocumentAttachmentForm: React.FC<DocumentAttachmentFormProps> = ({
  anexo,
  onSubmit,
  onCancel,
  isSubmitting = false,
  erro,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DocumentAttachmentFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: paraValores(anexo),
  });

  return (
    <Box component="form" onSubmit={handleSubmit((valores) => onSubmit(paraPayload(valores)))} noValidate>
      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Identificação
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth required>
              <InputLabel id="anexo-documento">Documento</InputLabel>
              <Controller
                name="documentType"
                control={control}
                render={({ field }) => (
                  <Select {...field} labelId="anexo-documento" label="Documento" disabled={isSubmitting}>
                    {TIPOS_COM_ANEXO.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>
                        {ROTULO_DO_DOCUMENTO[tipo]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Título"
                  fullWidth
                  required
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message || 'Sai impresso depois de "ANEXO". Ex.: Audiometria'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="normReference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Norma"
                  fullWidth
                  error={Boolean(errors.normReference)}
                  helperText={errors.normReference?.message || 'Ex.: NR-7'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name="numeral"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número"
                  fullWidth
                  error={Boolean(errors.numeral)}
                  helperText={errors.numeral?.message || 'Ex.: II — sai como "ANEXO II" no título do anexo'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <Controller
              name="displayOrder"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Ordem"
                  type="number"
                  fullWidth
                  required
                  error={Boolean(errors.displayOrder)}
                  helperText={errors.displayOrder?.message || 'Menor sai primeiro'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth required>
              <InputLabel id="anexo-situacao">Situação</InputLabel>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Select {...field} labelId="anexo-situacao" label="Situação" disabled={isSubmitting}>
                    <MenuItem value="true">Ativo</MenuItem>
                    <MenuItem value="false">Inativo</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Texto
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Controller
          name="content"
          control={control}
          render={({ field }) => (
            <EditorDeTextoRico
              label="Texto do anexo"
              required
              value={field.value ?? ''}
              onChange={field.onChange}
              textoGuia="Escreva aqui o texto do anexo, com as tabelas que ele tiver."
              error={Boolean(errors.content)}
              helperText={errors.content?.message ?? 'O título "ANEXO – …" sai sozinho; não repita aqui.'}
              disabled={isSubmitting}
            />
          )}
        />
      </Paper>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button type="button" onClick={onCancel} variant="outlined" startIcon={<CancelIcon />} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSubmitting}
        >
          {anexo ? 'Salvar anexo' : 'Criar anexo'}
        </Button>
      </Box>
    </Box>
  );
};

export default DocumentAttachmentForm;
